import React, { useEffect, useRef, useState } from 'react'
import { createAppeal, type AppealPayload } from '../../api/moderation'

export default function AppealModal({
  open,
  onClose,
  onSuccess,
  contentType,
  contentId,
  title,
}: {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  contentType: AppealPayload['content_type']
  contentId: string
  title?: string
}) {
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; message: string } | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 2400)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setToast(null)
    try {
      await createAppeal({
        content_type: contentType,
        content_id: contentId,
        message: message.trim(),
      })
      setToast({ tone: 'success', message: 'Appeal submitted. We will review it soon.' })
      setMessage('')
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null
        onSuccess?.()
        onClose()
      }, 1200)
    } catch (e: any) {
      const status = e?.response?.status
      const msg = status === 429
        ? "You've hit the daily appeal limit. Try again tomorrow."
        : e?.response?.data?.detail || e?.message || 'Failed to submit appeal.'
      setToast({ tone: 'error', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  function close() {
    if (submitting) return
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setToast(null)
    onClose()
  }

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="md:w-[520px] w-full bg-neutral-950 border-t md:border border-white/10 rounded-t-2xl md:rounded-2xl p-4">
          <div className="w-10 h-1 rounded bg-white/20 mx-auto mb-2 md:hidden" />
          <div className="flex items-center justify-between mb-2">
            <div className="text-white font-extrabold text-lg">Appeal</div>
            <button onClick={close} className="px-2 py-1 rounded-md text-white/90 hover:bg-white/10">X</button>
          </div>
          {title && <div className="text-sm text-white/70 mb-3">{title}</div>}
          <label className="block mb-3">
            <div className="text-white/70 text-sm mb-1">Message</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg bg-black border border-white/20 px-3 py-2 text-white min-h-[120px]"
              placeholder="Share why you think this should be reconsidered."
            />
          </label>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={close} disabled={submitting} className="px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10 disabled:opacity-60">X</button>
            <button onClick={submit} disabled={submitting || !message.trim()} className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold disabled:opacity-60">
              {submitting ? 'Submitting...' : 'Submit appeal'}
            </button>
          </div>
        </div>
      </div>
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[60]">
          <div
            className={`px-4 py-2 rounded-full border text-sm ${
              toast.tone === 'success'
                ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
                : 'border-rose-400/40 bg-rose-500/15 text-rose-100'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}
